import { useQuery } from "@tanstack/react-query";
import queryString from "query-string";
import React, { useCallback, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container as ContainerBootstrap,
  FormControl,
  FormGroup,
  FormLabel,
  Modal,
  Nav,
  Tab,
} from "react-bootstrap";
import { Control, Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory, useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { withAuthorizationVerifier } from "../../../components/authorization-verifier";
import ContentWrapper from "../../../components/content-wrapper";
import { FullWidthLoader } from "../../../components/full-width-loader";
import { useLocaleProvider } from "../../../components/locale-provider";
import { usePropertyData } from "../../../components/property-admin-data-provider";
import { usePropertyLocales } from "../../../components/property-locale-provider";
import { useProperty } from "../../../components/property-provider";
import { SectionHeading } from "../../../components/section-heading";
import { BundleAPIPromiseClient } from "../../../gen/witbooking/backoffice/bundle/v1/bundle_api_grpc_web_pb";
import {
  CreateBundleRequest,
  GetBundleRequest,
  UpdateBundleRequest,
} from "../../../gen/witbooking/backoffice/bundle/v1/bundle_api_pb";
import { decodeRptToken, useUserAuthInfo } from "../../../lib/auth";
import { useConfig } from "../../../lib/config";
import { useGrpcClient, usePropertyGrpcCall } from "../../../lib/grpc";
import { useApiClient, useBackofficeClient } from "../../../lib/http";
import { regenPropertyCache } from "../../common/api";
import { tickerHookFormValidation } from "../../shared/crud/form-validation";
import { getDataByActions, Image, renderImageComponent } from "../../shared/widgets/ImageManager";
import { TranslationComponent } from "../../shared/widgets/TranslationComponent";
import SmartSelector from "../../witadmin/CRUD/Common/SmartSelectorV2";
import {
  BundleRestrictionsForm,
  BundleRestrictionsFormType,
} from "../components/BundleRestrictionsForm";
import { InputMultiCheckbox } from "../components/InputMultiCheckbox";
import { InputToggle } from "../components/InputToggle";
import { OpenBooking } from "../components/OpenBooking";
import { RangeDateGrid } from "../components/RangeDateGrid";
import { SelectGroup } from "../components/SelectGroup";
import { getAllowedInventoryLines } from "../formConfiguration";
import { filterMessages } from "../messages";
import { useExtras, usePromotions } from "./hooks";
import {
  mapToGRPCFromObject,
  mapToObjectFromGRPC,
  transformMultimediaToImageManageObject,
} from "./mapper";
import {
  BUNDLE_ALL_PRICE_TYPE_MESSAGE,
  BUNDLE_WITHOUT_OWN_PRICE_TYPE_MESSAGE,
  messages,
} from "./messages";
import {
  Container,
  ErrorField,
  PackagesContainer,
  StyledBackButton,
  StyledCard,
  StyledTabs,
  WitLabel,
} from "./styles";
import {
  BundlePriceType,
  CRUD_MODE,
  CustomBundle,
  FormType,
  OtherValuesType,
  RangeDateWindowsType,
} from "./types";

const TICKER_MAX_LENGTH = 8;

const defaultBundle: CustomBundle = {
  id: "",
  ticker: "",
  active: false,
  inventoryLinesList: [],
  promotionsList: [],
  extrasList: [],
  travelWindowsList: [],
  codesList: [],
  bundlePriceType: BundlePriceType[BundlePriceType.BUNDLE_TYPE_DYNAMIC],
  translationsList: [],
  multimediaList: [],
  bundlePrice: 0,
  restrictions: {},
};

const defaultTravelWindows: RangeDateWindowsType = {
  id: "",
  startDate: null,
  endDate: null,
  weekDays: [],
};

function EditBundle() {
  const intl = useIntl();
  const location = useLocation();
  const config = useConfig();
  const property = useProperty();
  const locale = useLocaleProvider();
  const shortLocale = locale?.currentLocale?.substring(0, 2);
  const history = useHistory();
  const userAuthInfo = useUserAuthInfo();
  const decoded = decodeRptToken(property.token);
  const bundleId = useMemo(() => location.pathname.split("/").filter(Boolean).pop(), [
    location.pathname,
  ]);
  const allowedLocales = usePropertyLocales();

  const [imageFiles, setImageFiles] = useState<any[]>([]);
  const [travelWindows, setTravelWindows] = useState<RangeDateWindowsType[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalTravelWindowsValues, setModalTravelWindowsValues] = useState(defaultTravelWindows);
  const [modalTravelWindowsOpen, setModalTravelWindowsOpen] = useState(false);
  const [writeError, setWriteError] = useState(false);

  // GRPC
  const grpcClient = useGrpcClient(BundleAPIPromiseClient);
  const getBundle = usePropertyGrpcCall(grpcClient, grpcClient.getBundle);
  const createBundle = usePropertyGrpcCall(grpcClient, grpcClient.createBundle, decoded);
  const updateBundle = usePropertyGrpcCall(grpcClient, grpcClient.updateBundle, decoded);

  //REST
  const httpClient = useBackofficeClient();
  const httpClientCache = useApiClient();

  const { extras } = useExtras(property.ticker);
  const { promotions } = usePromotions(property.ticker);

  const { data: bundle, isLoading: isLoadingBundle } = useQuery({
    queryKey: ["bundles", bundleId],
    queryFn: async () => {
      const storedBundle = window.sessionStorage.getItem(`duplicate_preloaded_${bundleId}`);
      if (storedBundle) {
        const bundle = mapToObjectFromGRPC(JSON.parse(storedBundle), allowedLocales);
        setImageFiles([...bundle.multimediaList]);
        setTravelWindows([...bundle.travelWindowsList]);
        return {
          ...bundle,
          ticker: "",
          id: "",
          bundlePriceType: BundlePriceType[bundle?.bundlePriceType],
        };
      }

      if (!bundleId) return defaultBundle;

      const request = new GetBundleRequest();
      request.setBundleId(bundleId);

      const response = await getBundle(request);
      const bundle = mapToObjectFromGRPC(response.getBundle()?.toObject()!!, allowedLocales);
      setImageFiles([...bundle.multimediaList]);
      setTravelWindows([...bundle.travelWindowsList]);

      return bundle;
    },
    enabled: Boolean(bundleId),
    refetchOnWindowFocus: false,
  });

  const mode = useMemo(() => (bundle?.ticker ? CRUD_MODE.EDIT : CRUD_MODE.ADD), [bundle]);

  const getPriceTypes = useCallback(
    (value: string) => {
      if (userAuthInfo?.isWitAdmin) {
        return BUNDLE_ALL_PRICE_TYPE_MESSAGE;
      }

      // if bundle already has an own price type, own type must be listed
      return value === "BUNDLE_TYPE_OWN"
        ? BUNDLE_ALL_PRICE_TYPE_MESSAGE
        : BUNDLE_WITHOUT_OWN_PRICE_TYPE_MESSAGE;
    },
    [userAuthInfo?.isWitAdmin]
  );

  const handleOnBack = useCallback(() => history.push(`/${property.ticker}/bundles`), [
    history,
    property.ticker,
  ]);

  const methods = useForm<FormType>({
    mode: "onBlur",
    values: {
      ticker: bundle?.ticker || defaultBundle?.ticker,
      active: bundle?.active || defaultBundle?.active,
      bundlePriceType: bundle?.bundlePriceType || defaultBundle?.bundlePriceType,
      codesList: bundle?.codesList?.join(";") || defaultBundle?.codesList.join(";"),
      extrasList: bundle?.extrasList || defaultBundle?.extrasList,
      promotionsList: bundle?.promotionsList || defaultBundle?.promotionsList,
      inventoryLinesList: bundle?.inventoryLinesList || defaultBundle?.inventoryLinesList,
      translationsList: bundle?.translationsList || defaultBundle?.translationsList,
      bundlePrice: bundle?.bundlePrice || defaultBundle?.bundlePrice,
      restrictions: bundle?.restrictions || defaultBundle?.restrictions,
    },
  });

  const watchBundleType = useWatch({ control: methods.control, name: "bundlePriceType" });

  const propertyData = usePropertyData();
  const allowedInventoryLines = useMemo(() => getAllowedInventoryLines(propertyData), [
    propertyData,
  ]);

  const handleHideModal = useCallback(() => {
    setModalTravelWindowsOpen(false);
  }, [setModalTravelWindowsOpen]);

  const handleOpenTravelWindowsModal = useCallback(
    (action: string) => (id: string | undefined): void => {
      if (action === "edit") {
        const element = travelWindows.find((i: RangeDateWindowsType) => i.id === id);
        element
          ? setModalTravelWindowsValues(element)
          : setModalTravelWindowsValues(defaultTravelWindows);
        setModalTravelWindowsOpen(true);
        return;
      }
      setModalTravelWindowsValues(defaultTravelWindows);
      setModalTravelWindowsOpen(true);
    },
    [travelWindows]
  );

  const fetchCreateImages = useCallback(
    async (bundleId: string, data: Image[]) => {
      if (data.length) {
        const formData = new FormData();
        data.forEach((d: any) => {
          formData.append("images", d, d.id);
          formData.append(
            "medias",
            new Blob(
              [
                JSON.stringify({
                  id: d.id,
                  filename: d.filename.toLowerCase(),
                  translations: transformMultimediaToImageManageObject(d.translations),
                }),
              ],
              {
                type: "application/json",
              }
            )
          );
        });

        await httpClient.post<any, any>(
          `/v1/${property.ticker}/package/${bundleId}/media/multi`,
          formData
        );
      }
    },
    [httpClient, property.ticker]
  );

  const fetchUpdateImages = useCallback(
    async (bundleId: string, data: Image[]) => {
      if (data.length) {
        const request = data.map((d: any) => ({
          ...d,
          translations: transformMultimediaToImageManageObject(d.translations),
        }));
        await httpClient.put<any, any>(`/v1/${property.ticker}/package/${bundleId}/media`, request);
      }
    },
    [httpClient, property.ticker]
  );

  const fetchRemoveImages = useCallback(
    async (bundleId: string, data: Image[]) => {
      if (data.length) {
        const request = data.map(d => d.id);
        await httpClient.delete<any>(
          `/v1/${property.ticker}/package/${bundleId}/media?${queryString.stringify({
            mediaIds: request,
          })}`
        );
      }
    },
    [httpClient, property.ticker]
  );

  const fetchUploadImages = useCallback(
    async ({ id, multimediaList }: { id: string; multimediaList: any }) => {
      if (!bundle) {
        return await fetchCreateImages(id, multimediaList);
      }
      const { create, update, remove } = getDataByActions(bundle.multimediaList, multimediaList);

      return await Promise.all([
        fetchCreateImages(id, create),
        fetchRemoveImages(id, remove),
        fetchUpdateImages(id, update),
      ]);
    },
    [bundle, fetchCreateImages, fetchRemoveImages, fetchUpdateImages]
  );

  const handleCreate = useCallback(
    async (formValues: FormType) => {
      try {
        const otherValues: OtherValuesType = {
          id: uuidv4(),
          travelWindows: travelWindows,
          multimediaList: imageFiles,
        };

        setLoading(true);
        setWriteError(false);

        const request = new CreateBundleRequest();
        request.setBundle(mapToGRPCFromObject(formValues, otherValues));

        const response = await createBundle(request);
        await fetchUploadImages(otherValues);
        await regenPropertyCache(httpClientCache, property.ticker);
        history.push(`/${property.ticker}/bundles`);

        return response.getBundle()?.getId()!!;
      } catch (error) {
        setWriteError(true);
        console.error("Error creating bundle:", error);
      } finally {
        setLoading(false);
      }
    },
    [
      createBundle,
      fetchUploadImages,
      history,
      httpClientCache,
      imageFiles,
      property.ticker,
      travelWindows,
    ]
  );

  const handleUpdate = useCallback(
    async (formValues: FormType) => {
      if (!bundle) {
        throw new Error("bundle is required to update");
      }

      try {
        const otherValues = {
          id: bundle.id,
          travelWindows: [...travelWindows],
          multimediaList: imageFiles,
        };

        setLoading(true);
        setWriteError(false);

        const request = new UpdateBundleRequest();
        request.setBundle(mapToGRPCFromObject(formValues, otherValues));

        await updateBundle(request);
        await fetchUploadImages(otherValues);
        await regenPropertyCache(httpClientCache, property.ticker);

        return history.push(`/${property.ticker}/bundles`);
      } catch (error) {
        setWriteError(true);
        console.error("Error updating bundle:", error);
      } finally {
        setLoading(false);
      }
    },
    [
      bundle,
      fetchUploadImages,
      history,
      httpClientCache,
      imageFiles,
      property.ticker,
      travelWindows,
      updateBundle,
    ]
  );

  const handleOnSaveTravelWindows = useCallback(
    (value: RangeDateWindowsType) => {
      const rangeWindow: RangeDateWindowsType = {
        id: uuidv4(),
        weekDays: value.weekDays,
        startDate: value.startDate,
        endDate: value.endDate,
      };
      const result = travelWindows.filter((item: RangeDateWindowsType) => item.id !== value.id);

      setTravelWindows([...result, rangeWindow]);
      setModalTravelWindowsOpen(false);
    },
    [travelWindows]
  );

  const handleDeleteConfirm = useCallback(
    (id: string) => {
      const result = travelWindows.filter((item: RangeDateWindowsType) => item.id !== id);
      setTravelWindows([...result]);
    },
    [travelWindows]
  );

  const getDisplayMultiCheckbox = (locale: string) => (option: {
    translations: { [key: string]: { name: string; description: string } };
  }) => option.translations[locale]?.name;

  const getSecondDisplayMultiCheckbox = (option: {
    [key: string]: { name: string; description: string };
  }) => option?.ticker;

  const handleImages = (e: any[]) => {
    setImageFiles([...e]);
  };

  const title = intl.formatMessage(messages.fileManager);
  const getImageRenderer = useMemo(
    () =>
      renderImageComponent({
        schema: {
          title: title,
        },
        idSchema: { $id: "" },
        formData: bundle?.multimediaList,
        onChange: handleImages,
        config: {
          locales: allowedLocales,
          maxSizeMB: config.mediaMaxSizeMB,
          maxFiles: config.mediaMaxFiles,
          minWidth: config.mediaMinWidth,
          helpText: {
            id: messages.imagesHelpText.id,
            defaultMessage: messages.imagesHelpText.defaultMessage,
            values: {
              maxFiles: config.mediaMaxFiles || 10,
            },
          },
        },
      }),
    [
      allowedLocales,
      bundle?.multimediaList,
      config.mediaMaxFiles,
      config.mediaMaxSizeMB,
      config.mediaMinWidth,
      title,
    ]
  );

  const handlerAction = mode === CRUD_MODE.ADD ? handleCreate : handleUpdate;

  if (loading || isLoadingBundle) {
    return <FullWidthLoader />;
  }

  return (
    <ContentWrapper>
      <FormProvider {...methods}>
        <PackagesContainer>
          <SectionHeading>
            {mode === CRUD_MODE.ADD ? (
              <FormattedMessage {...messages.createPackage} />
            ) : (
              <FormattedMessage {...messages.editPackage} />
            )}
          </SectionHeading>
          <ContainerBootstrap fluid={true}>
            <StyledTabs defaultActiveKey="properties">
              <StyledCard>
                <Card.Header>
                  <Nav variant="tabs">
                    <Nav.Item key="properties">
                      <Nav.Link eventKey="properties">
                        <FormattedMessage {...messages.properties} />
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item key="images">
                      <Nav.Link eventKey="images">
                        <FormattedMessage {...messages.images} />
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Card.Header>

                <Card.Body>
                  <Tab.Content>
                    <Tab.Pane key="properties" eventKey="properties">
                      <FormGroup as={Col}>
                        <FormLabel column sm={12}>
                          <WitLabel>
                            <FormattedMessage {...messages.active} />
                          </WitLabel>
                        </FormLabel>
                        <Col sm={2}>
                          <Controller
                            name="active"
                            control={methods.control}
                            render={({ field }) => (
                              <InputToggle className="witadmin-bs3" disabled={false} {...field} />
                            )}
                          />
                        </Col>
                      </FormGroup>
                      <FormGroup as={Col}>
                        <FormLabel column sm={12}>
                          <FormattedMessage {...messages.packageTicker} /> {"*"}
                        </FormLabel>
                        <Col sm={3}>
                          <Controller
                            name="ticker"
                            control={methods.control}
                            rules={tickerHookFormValidation(intl, TICKER_MAX_LENGTH)}
                            render={({ field }) => (
                              <FormControl
                                disabled={CRUD_MODE.EDIT === mode}
                                {...field}
                                maxLength={TICKER_MAX_LENGTH}
                              />
                            )}
                          />
                          {methods.formState.errors.ticker?.type === "required" && (
                            <ErrorField>
                              <FormattedMessage {...messages.presenceValidator} />
                            </ErrorField>
                          )}
                          {methods.formState.errors.ticker?.type === "pattern" && (
                            <ErrorField>
                              <FormattedMessage {...messages.tickerValdiation} />
                            </ErrorField>
                          )}
                          {methods.formState.errors.ticker?.type === "maxLength" && (
                            <ErrorField>
                              <FormattedMessage {...messages.maxLengthTickerValidation} />
                            </ErrorField>
                          )}
                        </Col>
                      </FormGroup>

                      <FormGroup as={Col}>
                        <Col sm={12}>
                          <TranslationComponent
                            allowedLocales={allowedLocales}
                            defaultValues={bundle?.translationsList}
                          />
                        </Col>
                      </FormGroup>

                      <FormGroup as={Col}>
                        <FormLabel column sm={12}>
                          <WitLabel>
                            <FormattedMessage {...messages.bundleType} />
                          </WitLabel>
                        </FormLabel>
                        <Col sm={4}>
                          <Controller
                            name="bundlePriceType"
                            control={methods.control}
                            render={({ field }) => (
                              <SelectGroup optionDisplay={getPriceTypes(field.value)} {...field} />
                            )}
                          />
                        </Col>
                      </FormGroup>
                      {watchBundleType === "BUNDLE_TYPE_FIXED" && (
                        <FormGroup as={Col}>
                          <Col sm={3}>
                            <Controller
                              rules={{
                                min: 1,
                              }}
                              name="bundlePrice"
                              control={methods.control}
                              render={({ field }) => <FormControl {...field} />}
                            />
                            {watchBundleType === "BUNDLE_TYPE_FIXED" &&
                              methods.formState.errors.bundlePrice?.type === "min" && (
                                <ErrorField>
                                  <FormattedMessage {...messages.moreThanZero} />
                                </ErrorField>
                              )}
                          </Col>
                        </FormGroup>
                      )}

                      <FormGroup as={Col}>
                        <FormLabel column sm={12}>
                          <WitLabel>
                            <FormattedMessage {...messages.promotions} />
                          </WitLabel>
                        </FormLabel>
                        <Col sm={12}>
                          <Controller
                            name="promotionsList"
                            control={methods.control}
                            render={({ field }) => (
                              <InputMultiCheckbox
                                getSecondDisplay={getSecondDisplayMultiCheckbox}
                                getDisplay={getDisplayMultiCheckbox(shortLocale)}
                                className="witadmin-bs3"
                                defaultValues={bundle?.promotionsList}
                                field={{
                                  fieldName: `promotionsMultiCheckbox`,
                                  options: promotions || [],
                                  selectables: true,
                                }}
                                {...field}
                              />
                            )}
                          />
                        </Col>
                      </FormGroup>

                      <FormGroup as={Col}>
                        <FormLabel column sm={12}>
                          <WitLabel>
                            <FormattedMessage {...messages.extras} />
                          </WitLabel>
                        </FormLabel>
                        <Col sm={12}>
                          <Controller
                            name="extrasList"
                            control={methods.control}
                            rules={{ required: true }}
                            render={({ field: { ref, ...field } }) => (
                              <InputMultiCheckbox
                                getSecondDisplay={getSecondDisplayMultiCheckbox}
                                getDisplay={getDisplayMultiCheckbox(shortLocale)}
                                locale={shortLocale}
                                defaultValues={bundle?.extrasList}
                                className="witadmin-bs3"
                                field={{
                                  fieldName: `extrasMultiCheckbox`,
                                  options: extras || [],
                                  selectables: true,
                                  entitySecondaryDisplayTranslated: true,
                                }}
                                {...field}
                              />
                            )}
                          />
                          {methods.formState.errors.extrasList?.type === "required" && (
                            <ErrorField>
                              <FormattedMessage {...messages.unlessOneExtraError} />
                            </ErrorField>
                          )}
                        </Col>
                      </FormGroup>

                      <FormGroup as={Col}>
                        <FormLabel column sm={12}>
                          <WitLabel>
                            <FormattedMessage {...messages.stayDates} />
                          </WitLabel>
                        </FormLabel>
                        <Col sm={12}>
                          {bundle?.travelWindowsList && (
                            <RangeDateGrid
                              list={travelWindows}
                              headers={[
                                intl.formatMessage(messages.startDate),
                                intl.formatMessage(messages.endDate),
                                intl.formatMessage(messages.weekDay),
                              ]}
                              onEdit={handleOpenTravelWindowsModal("edit")}
                              onDelete={handleDeleteConfirm}
                              deletable
                              editable
                            />
                          )}
                          <Button
                            variant="primary"
                            className="mt-3 mr-3"
                            onClick={() => handleOpenTravelWindowsModal("add")(undefined)}>
                            <FormattedMessage {...messages.addDatesRange} />
                          </Button>
                        </Col>
                      </FormGroup>

                      <BundleRestrictionsForm
                        control={
                          (methods.control as unknown) as Control<BundleRestrictionsFormType>
                        }
                        formState={methods.formState}
                      />

                      <FormGroup as={Col}>
                        <FormLabel column sm={12}>
                          <WitLabel>
                            <FormattedMessage {...messages.promoCodes} />
                          </WitLabel>
                        </FormLabel>
                        <Col sm={3}>
                          <Controller
                            rules={{
                              required: false,
                              pattern: /^\S*$/,
                            }}
                            name="codesList"
                            control={methods.control}
                            render={({ field }) => <FormControl {...field} />}
                          />
                          {methods.formState.errors.codesList?.type === "pattern" && (
                            <ErrorField>
                              <FormattedMessage {...messages.emtpySpacesValidator} />
                            </ErrorField>
                          )}
                        </Col>
                      </FormGroup>
                      <FormGroup as={Col}>
                        <FormLabel column sm={12}>
                          <WitLabel>
                            <FormattedMessage {...messages.packageFor} />
                          </WitLabel>
                        </FormLabel>
                        <Col sm={12}>
                          <Controller
                            name="inventoryLinesList"
                            control={methods.control}
                            rules={{
                              required: true,
                            }}
                            render={({ field: { ref, ...field } }) => (
                              <Container className="witadmin-bs3 witadmin-bs3-parent">
                                <SmartSelector
                                  initialValues={bundle?.inventoryLinesList}
                                  field={{
                                    disabled: false,
                                    fieldName: "inventoryLinesList",
                                    filterNames: [
                                      intl.formatMessage(filterMessages.accommodation),
                                      intl.formatMessage(filterMessages.conditions),
                                      intl.formatMessage(filterMessages.ocupation),
                                      intl.formatMessage(filterMessages.mealplan),
                                    ],
                                    defaultValue: [],
                                    options: allowedInventoryLines,
                                    staticFilters: {},
                                  }}
                                  {...field}
                                />
                              </Container>
                            )}
                          />
                          {methods.formState.errors.inventoryLinesList?.type === "required" && (
                            <ErrorField>
                              <FormattedMessage {...messages.unlessOneInventoryLineError} />
                            </ErrorField>
                          )}
                        </Col>
                        {Object.values(methods.formState.errors).length > 0 && (
                          <Col sm={5}>
                            <ErrorField>
                              <FormattedMessage {...messages.propertiesTabValidator} />
                            </ErrorField>
                          </Col>
                        )}
                      </FormGroup>
                      <FormGroup as={Col}>
                        {imageFiles.length < 1 && (
                          <Col sm={5}>
                            <ErrorField>
                              <FormattedMessage {...messages.emptyImageValidator} />
                            </ErrorField>
                          </Col>
                        )}
                        {writeError && (
                          <ErrorField>
                            <FormattedMessage {...messages.errorSavingBundle} />
                          </ErrorField>
                        )}
                        <Col sm={3}>
                          <Button
                            variant="primary"
                            className="mt-3 mr-3"
                            disabled={imageFiles.length < 1}
                            onClick={methods.handleSubmit(handlerAction)}>
                            <FormattedMessage {...messages.save} />
                          </Button>
                          <StyledBackButton className="mt-3" onClick={handleOnBack}>
                            {intl.formatMessage(messages.goBack)}
                          </StyledBackButton>
                        </Col>
                      </FormGroup>
                    </Tab.Pane>
                  </Tab.Content>
                  <Tab.Content>
                    <Tab.Pane key="images" eventKey="images">
                      <FormGroup as={Col}>
                        <Col sm={12}>{getImageRenderer}</Col>
                        {imageFiles.length < 1 && (
                          <Col sm={5}>
                            <ErrorField>
                              <FormattedMessage {...messages.emptyImageValidator} />
                            </ErrorField>
                          </Col>
                        )}
                        {Object.values(methods.formState.errors).length > 0 && (
                          <Col sm={5}>
                            <ErrorField>
                              <FormattedMessage {...messages.propertiesTabValidator} />
                            </ErrorField>
                          </Col>
                        )}
                      </FormGroup>

                      <Col sm={3}>
                        <Button
                          variant="primary"
                          className="mt-3 mr-3"
                          disabled={imageFiles.length < 1}
                          onClick={methods.handleSubmit(handlerAction)}>
                          <FormattedMessage {...messages.save} />
                        </Button>
                        <StyledBackButton className="mt-3" onClick={handleOnBack}>
                          {intl.formatMessage(messages.goBack)}
                        </StyledBackButton>
                      </Col>
                    </Tab.Pane>
                  </Tab.Content>
                </Card.Body>
              </StyledCard>
            </StyledTabs>
          </ContainerBootstrap>

          <Modal show={modalTravelWindowsOpen} onHide={handleHideModal}>
            <Modal.Header closeButton>
              <FormattedMessage {...messages.stayDates} />
            </Modal.Header>
            <Modal.Body>
              <OpenBooking
                disabled={false}
                field={{ isRange: true, startField: "startDate", endField: "endDate" }}
                defaultValues={modalTravelWindowsValues}
                onSave={handleOnSaveTravelWindows}
                intl={intl}
              />
            </Modal.Body>
          </Modal>
        </PackagesContainer>
      </FormProvider>
    </ContentWrapper>
  );
}

export default withAuthorizationVerifier(EditBundle);
